import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import './ProjectsList.css';
import ProjectCard from './ProjectCard/ProjectCard';
import { useProject } from '../../../contexts/ProjectContext';
import { useEC2InstanceStatus } from '../../../contexts/EC2InstanceStatusContext';
import RefreshButton from '../../RefreshButton/RefreshButton';
import useCachedApi from '../../../hooks/useCachedApi';
import { getProjectActivity, resolveUserEmail } from '../../../utils/projectActivity';
import { getPinnedProjects, setProjectPinned, comparePinnedFirst } from '../../../utils/projectPins';
import { nextSelectedTypes, ALL_TYPES_VALUE, filterPillLabel } from '../../../utils/projectFilters';

const FILTER_OPTIONS = [
  { value: 'all', label: 'Show All' },
  { value: 'rsa-model', label: 'RSA Model' },
  { value: 'rsa-existing', label: 'RSA Existing' },
  { value: 'visualization', label: 'Visualization' },
];

// The three real ProjectType values in dropdown order. Excludes the 'all'
// sentinel, which is a row in the menu but never a member of the selection.
const TYPE_VALUES = FILTER_OPTIONS
  .filter((o) => o.value !== ALL_TYPES_VALUE)
  .map((o) => o.value);

/**
 * Only sorts backed by a value that actually varies per project.
 *
 * Three options were removed because nothing fed them — each was a no-op that
 * fell through to the server's `ORDER BY p.id DESC`, so it looked like a sort
 * and behaved like "newest first". Don't reinstate one without first shipping
 * the signal it needs:
 *
 * - "Last model update" (`ModelModified`) — both endpoints send it as the
 *   project's `created` date, identical to `Modified`, and
 *   formatProjectForFrontend re-derives it from `created` regardless. It was
 *   also excluded from ACTIVITY_SORT_KEYS, so unlike "Last update" it got no
 *   local-activity overlay. Needs the model folder's Drive modifiedTime, or a
 *   column written when a model run finishes.
 * - "Role" (`UserRole`) — hardcoded to "Administrator" for every project by
 *   both endpoints, and re-defaulted to the same string client-side. Needs real
 *   per-user roles from `projectusers`.
 * - "Project Size" (`Size`) — hardcoded to 0 the same way. Needs a real
 *   byte/mileage total.
 *
 * A fourth, "Last update" (`Modified`), went for a different reason: it DID
 * work, but it duplicated "Last Viewed" off the same localStorage signal and
 * lost the tie-break. For a never-opened project it fell back to the creation
 * date, so opened and never-opened projects interleaved on two unrelated
 * clocks — a project created yesterday outranked one you opened last week.
 * "Last Viewed" falls back to epoch instead, sinking never-opened projects to
 * the bottom and keeping the top of the list honest.
 */
const SORT_OPTIONS = [
  { value: 'Name|Project Name|ASC', label: 'Project Name' },
  { value: 'LastViewed|Last Viewed|DESC', label: 'Last Viewed' },
];

/**
 * Sort keys that lift recently-opened projects, via `activityMap`.
 *
 * Deliberately just the one. The rule this encodes: an EXPLICIT action (pinning)
 * overrides whatever sort is active, but an IMPLICIT signal (opening a project)
 * only reorders when the user asked to be sorted by recency. That is why
 * "Project Name" stays strictly alphabetical — a sort that promises A-Z and then
 * floats cards out of order can be neither scanned nor trusted, and pinning is
 * the escape hatch for wanting something on top regardless.
 */
const ACTIVITY_SORT_KEYS = new Set(['LastViewed']);

/**
 * Nearest ancestor of `node` that actually scrolls vertically, falling back to the
 * document scroller.
 *
 * Resolved at runtime rather than hardcoded. It does currently resolve to
 * `.projects-content`, but that was worth two wrong guesses to establish: the
 * markup nests two candidates, and `.projects-container` declares `overflow-y:
 * auto` and then `overflow-y: hidden` in the same rule, so which one scrolls is
 * not obvious from reading either stylesheet. Requiring BOTH a scrolling
 * `overflow-y` and real overflow (`scrollHeight > clientHeight`) is what makes
 * this answer correct without depending on that reading staying true.
 */
const findScroller = (node) => {
  let el = node?.parentElement;
  while (el && el !== document.body && el !== document.documentElement) {
    const overflowY = window.getComputedStyle(el).overflowY;
    if (/(auto|scroll|overlay)/.test(overflowY) && el.scrollHeight > el.clientHeight + 1) {
      return el;
    }
    el = el.parentElement;
  }
  return document.scrollingElement || document.documentElement;
};

const isDocumentScroller = (el) =>
  el === document.scrollingElement || el === document.documentElement || el === document.body;

/** Distance from the top of the scroller's content to the top of `card`. */
const cardTopWithin = (card, scroller) => {
  const cardTop = card.getBoundingClientRect().top;
  return isDocumentScroller(scroller)
    ? cardTop + scroller.scrollTop
    : scroller.scrollTop + cardTop - scroller.getBoundingClientRect().top;
};

/**
 * Coerce a date-ish value to a Date, or null if it isn't one.
 *
 * Needed because these fields are Dates when they come straight from
 * projectService, but ISO strings when they come from the sessionStorage cache
 * (JSON round-trip). Comparing a Date against a string returns false in both
 * directions, so a mixed sort silently collapses to "no order at all".
 */
const toActivityDate = (value) => {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getProjectsCacheKey = (currentUser) => {
  const email =
    currentUser?.attributes?.email ||
    currentUser?.email ||
    currentUser?.Email ||
    sessionStorage.getItem('userEmail') ||
    'anonymous';
  return `projectsListCache:${String(email).trim().toLowerCase()}`;
};

const readProjectsCache = (cacheKey) => {
  try {
    const raw = sessionStorage.getItem(cacheKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.projects) ? parsed.projects : [];
  } catch (error) {
    console.warn('Failed to read projects list cache:', error);
    sessionStorage.removeItem(cacheKey);
    return [];
  }
};

const writeProjectsCache = (cacheKey, projectsToCache) => {
  try {
    const payload = {
      projects: projectsToCache,
      cachedAt: Date.now()
    };
    sessionStorage.setItem(cacheKey, JSON.stringify(payload));
  } catch (error) {
    console.warn('Failed to persist projects list cache:', error);
  }
};

const ProjectsList = ({ projects = [], loading, onProjectSelect, onViewProject, onRefresh, user, customRedirectPaths = {} }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  // Real ProjectType values only. Empty means "Show All" — that row is derived
  // from `size === 0` rather than stored, so there is nothing to keep in sync.
  // Not persisted, matching the previous behaviour: a type filter is transient
  // view state, unlike pins.
  const [selectedTypes, setSelectedTypes] = useState(() => new Set());

  const filterLabel = useMemo(
    () => filterPillLabel(selectedTypes, FILTER_OPTIONS),
    [selectedTypes],
  );
  const [sortBy, setSortBy] = useState('LastViewed'); // 'Name', 'LastViewed'
  const [sortOrder, setSortOrder] = useState('DESC'); // 'ASC', 'DESC'
  // Lands a returning user on what they were last working in. A first-time user
  // has an empty activity map, so every project ties at epoch and the stable sort
  // falls through to the server's `ORDER BY p.id DESC` — newest first, which is
  // what they saw under the old "Last update" default anyway.
  const [sortOptionValue, setSortOptionValue] = useState('LastViewed|Last Viewed|DESC');
  const [openDropdown, setOpenDropdown] = useState(null); // null | 'filter' | 'sort'
  const [isRefreshing, setIsRefreshing] = useState(false);
  const projectsCacheKey = useMemo(() => getProjectsCacheKey(user), [user]);
  const [cachedProjects, setCachedProjects] = useState(() => readProjectsCache(getProjectsCacheKey(user)));
  const [fetchProjects, setExistingProjectName] = useState("");
  const filterDropdownRef = useRef(null);
  const sortDropdownRef = useRef(null);
  // Mobile: the toolbar is hidden until the bottom bar's search icon summons
  // it (the shell dispatches this window event — it has no other coupling to
  // this screen). Opening focuses the input in the same gesture.
  const [mobileToolbarOpen, setMobileToolbarOpen] = useState(false);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const onToggle = () => {
      setMobileToolbarOpen((v) => {
        const next = !v;
        if (next) {
          setTimeout(() => searchInputRef.current?.focus(), 60);
        }
        return next;
      });
    };
    window.addEventListener('rsa:mobile-search-toggle', onToggle);
    return () => window.removeEventListener('rsa:mobile-search-toggle', onToggle);
  }, []);

  // Bottom bar's refresh icon. Ref indirection so the mount-once listener
  // always calls the latest handleRefresh closure.
  const handleRefreshRef = useRef(null);
  useEffect(() => {
    const onMobileRefresh = () => handleRefreshRef.current?.();
    window.addEventListener('rsa:mobile-refresh', onMobileRefresh);
    return () => window.removeEventListener('rsa:mobile-refresh', onMobileRefresh);
  }, []);
  
  // Use project context utilities
  const { formatDate, formatFileSize, isProjectExpired, canViewProject, canAccessProjectFiles } = useProject();
  
  // Use EC2 instance status context for polling
  const { startPolling, stopPolling, updateProjects } = useEC2InstanceStatus();
  
  // Memoize startPolling and stopPolling to prevent infinite loops
  const startPollingRef = useRef(startPolling);
  const stopPollingRef = useRef(stopPolling);
  
  useEffect(() => {
    startPollingRef.current = startPolling;
    stopPollingRef.current = stopPolling;
  }, [startPolling, stopPolling]);

  // Switch cache bucket when user changes.
  useEffect(() => {
    setCachedProjects(readProjectsCache(projectsCacheKey));
  }, [projectsCacheKey]);

  // Keep cache synced with fresh projects payload.
  useEffect(() => {
    if (Array.isArray(projects) && projects.length > 0) {
      setCachedProjects(projects);
      writeProjectsCache(projectsCacheKey, projects);
    }
  }, [projects, projectsCacheKey]);

  const projectsToRender = useMemo(() => {
    if (Array.isArray(projects) && projects.length > 0) {
      return projects;
    }
    return cachedProjects;
  }, [projects, cachedProjects]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        openDropdown === 'filter' && filterDropdownRef.current && !filterDropdownRef.current.contains(e.target) ||
        openDropdown === 'sort' && sortDropdownRef.current && !sortDropdownRef.current.contains(e.target)
      ) {
        setOpenDropdown(null);
      }
    };
    if (openDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openDropdown]);

  // Debounce search term with shorter delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 50); // Reduced to 50ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Memoized filtering and sorting for better performance
  // Read once per mount. Projects.jsx swaps currentView when a project opens, so
  // this component unmounts and remounts on the way back — a just-recorded open is
  // always picked up without any cross-component signalling.
  const activityMap = useMemo(() => getProjectActivity(resolveUserEmail(user)), [user]);

  // Pins, unlike activity, are toggled while this component is MOUNTED — so they
  // live in state, not a mount-time memo, and the grid re-sorts on the click.
  // Seeded lazily so the correct order is there on first paint (no reshuffle).
  const [pinnedIds, setPinnedIds] = useState(() => getPinnedProjects(resolveUserEmail(user)));

  // Card that was just PINNED (not unpinned) and should be followed to its new
  // position. Consumed by the FLIP effect below, which owns the scrolling because it
  // already measures post-reorder geometry.
  const followCardIdRef = useRef(null);
  // Scroll offset captured at CLICK time, before React reorders the DOM. If this
  // differs from the offset seen in the layout effect, the browser moved the
  // Scroll offset captured at CLICK time, before React reorders the DOM. The layout
  // effect restores it, because the browser will have moved the viewport by then —
  // see the comment at the restore site for why.
  const scrollTopAtClickRef = useRef(null);

  // `user` resolves asynchronously; re-seed once the real identity lands so we are
  // not stuck showing the 'anonymous' bucket. Same reasoning as the `user` dep on
  // Projects.jsx's recordProjectOpened effect.
  // Costs one extra render on mount (a fresh Set with identical contents is still
  // a new identity). That is accepted rather than guarded — the alternative is a
  // content-comparison that runs on every user change to save a single re-render.
  useEffect(() => {
    setPinnedIds(getPinnedProjects(resolveUserEmail(user)));
  }, [user]);

  // Derives `next` from pinnedIds rather than using a setState updater: the write
  // to localStorage is a side effect, and StrictMode double-invokes updaters in dev.
  const handleTogglePin = useCallback((projectId) => {
    const email = resolveUserEmail(user);
    const next = !pinnedIds.has(String(projectId));
    setPinnedIds(setProjectPinned(projectId, email, next));
    // Only follow on PIN. Unpinning sends the card back down into the pack, and
    // chasing it there would drag the viewport somewhere the user never asked to go.
    followCardIdRef.current = next ? String(projectId) : null;
    // Must be read HERE, before React reorders the DOM — by the time the layout
    // effect runs the browser has already moved it.
    const liveScroller = gridRef.current ? findScroller(gridRef.current) : null;
    scrollTopAtClickRef.current = liveScroller?.scrollTop ?? null;
  }, [pinnedIds, user]);

  const filteredProjects = useMemo(() => {
    if (!projectsToRender || !Array.isArray(projectsToRender)) {
      return [];
    }

    let filtered = [...projectsToRender];

    // Apply search filter
    if (debouncedSearchTerm) {
      const searchLower = debouncedSearchTerm.toLowerCase();
      filtered = filtered.filter(project =>
        project.Name.toLowerCase().includes(searchLower) ||
        project.Plan.toLowerCase().includes(searchLower) ||
        project.UserRole.toLowerCase().includes(searchLower) ||
        // Project ID — the value the card surfaces as `.role` ("ID: 2437").
        // Stringified because Id arrives as a number; substring rather than
        // exact so a partial "24" narrows the list the way the other fields do.
        String(project.Id ?? '').includes(searchLower)
      );
    }

    // Apply type filter. An empty selection is "Show All", so it filters nothing.
    if (selectedTypes.size > 0) {
      filtered = filtered.filter((project) => selectedTypes.has(project.ProjectType));
    }

    // "Last Viewed" sorts by activity. Nothing writes the server's `lastviewed`
    // column, so today the only live signal is the locally-recorded open time;
    // fold it in and keep whichever is newer. max() rather than a straight
    // override so this stays correct once the backend does start sending a real
    // activity timestamp: whichever side is fresher wins, and nothing here needs
    // changing.
    const sortValueFor = (project) => {
      if (!ACTIVITY_SORT_KEYS.has(sortBy)) return project[sortBy];

      // Everything on an activity key leaves here as a Date, so the sort below
      // never compares a Date against a string.
      const baseDate = toActivityDate(project[sortBy]);
      const recordedDate = toActivityDate(activityMap[String(project.Id)]);

      if (!recordedDate) return baseDate ?? new Date(0);
      if (!baseDate) return recordedDate;
      return recordedDate > baseDate ? recordedDate : baseDate;
    };

    // Apply sorting
    filtered.sort((a, b) => {
      // Pinned projects form a block above everything, under every sort mode.
      // MUST come before the ASC/DESC branch below, or an ascending sort would
      // invert this and drop the pinned block to the bottom.
      const pinCmp = comparePinnedFirst(pinnedIds, a, b);
      if (pinCmp !== 0) return pinCmp;

      let aValue = sortValueFor(a);
      let bValue = sortValueFor(b);

      // Handle date fields
      if (aValue instanceof Date && bValue instanceof Date) {
        aValue = aValue.getTime();
        bValue = bValue.getTime();
      }

      // Handle string fields
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortOrder === 'ASC') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    return filtered;
  }, [projectsToRender, debouncedSearchTerm, selectedTypes, sortBy, sortOrder, activityMap, pinnedIds]);

  // FLIP: animate cards from where they were to where they now are, so a pinned
  // card can be followed to the top instead of teleporting.
  //
  // Positions come from offsetLeft/offsetTop, NOT getBoundingClientRect — the
  // previous frame's values are recorded in an earlier layout pass, and rect
  // coordinates are viewport-relative, so a scroll in between would corrupt every
  // delta. Offsets are relative to the offsetParent and survive scrolling.
  const gridRef = useRef(null);
  const cardOffsetsRef = useRef(new Map());
  const renderedOrderRef = useRef(null);
  // Teardown for an in-flight follow-scroll: cancels the frame loop and unhooks the
  // hand-back-to-user listeners. Null when nothing is animating.
  const stopScrollRef = useRef(null);

  // Elevator curve: accelerate out of the start, cruise, decelerate into the stop.
  //
  // This is hand-rolled rather than scrollTo({ behavior: 'smooth' }) because the
  // native version caps its duration near 300ms with a fixed curve — over a long
  // list that covers thousands of pixels in a third of a second, which reads as a
  // teleport rather than a scroll, and there is no way to slow it down.
  const animateScrollTo = useCallback((scroller, targetTop) => {
    stopScrollRef.current?.();

    const startTop = scroller.scrollTop;
    const distance = targetTop - startTop;
    if (Math.abs(distance) < 1) return;

    // Longer trips take longer, but bounded at both ends: short hops still ease
    // rather than snap, and a 50-project list never feels like it is dawdling.
    const duration = Math.min(900, Math.max(380, Math.abs(distance) * 0.5));
    const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

    let frame = null;
    let startTime = null;

    const stop = () => {
      if (frame !== null) cancelAnimationFrame(frame);
      scroller.removeEventListener('wheel', stop);
      scroller.removeEventListener('touchstart', stop);
      stopScrollRef.current = null;
    };

    // The moment the user scrolls themselves, hand control back — nothing is more
    // annoying than a page that keeps dragging you somewhere you just left.
    scroller.addEventListener('wheel', stop, { passive: true });
    scroller.addEventListener('touchstart', stop, { passive: true });
    stopScrollRef.current = stop;

    const step = (now) => {
      if (startTime === null) startTime = now;
      const progress = Math.min(1, (now - startTime) / duration);
      // Assigned unconditionally each frame. If the browser re-applies its
      // focus-into-view scroll on a later frame, this overwrites it rather than
      // letting the two fight — we own the offset until the animation ends.
      scroller.scrollTop = startTop + distance * easeInOutCubic(progress);
      if (progress < 1) {
        frame = requestAnimationFrame(step);
      } else {
        stop();
      }
    };
    frame = requestAnimationFrame(step);

    // Returned so the card slide can borrow it — the two must finish together or
    // the card settles while the viewport is still gliding, reading as two
    // unrelated motions instead of one.
    return duration;
  }, []);

  // Unmounting mid-flight would otherwise leave a frame loop writing to a detached
  // scroller. ProjectsList unmounts on every project open, so this is a live path.
  useEffect(() => () => stopScrollRef.current?.(), []);

  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid) {
      // Grid unmounted (empty/loading state) — drop stale geometry so the next
      // mount animates from its own first paint rather than from old positions.
      cardOffsetsRef.current = new Map();
      renderedOrderRef.current = null;
      return;
    }

    const cards = Array.from(grid.querySelectorAll('[data-project-id]'));
    const order = cards.map((card) => card.dataset.projectId).join(',');
    const previousOffsets = cardOffsetsRef.current;
    const orderChanged = renderedOrderRef.current !== null && renderedOrderRef.current !== order;
    renderedOrderRef.current = order;

    // Measure the settled layout BEFORE touching any transform, and record it for
    // the next reorder. Reading after inverting would capture the offset instead.
    const offsets = new Map();
    const moved = [];
    cards.forEach((card) => {
      const id = card.dataset.projectId;
      const left = card.offsetLeft;
      const top = card.offsetTop;
      offsets.set(id, { left, top });

      const previous = previousOffsets.get(id);
      if (!previous) return;
      const dx = previous.left - left;
      const dy = previous.top - top;
      // Sub-pixel drift from grid reflow is not a move worth animating.
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
      moved.push({ card, dx, dy });
    });
    cardOffsetsRef.current = offsets;

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    const followId = followCardIdRef.current;
    followCardIdRef.current = null;
    const scrollTopAtClick = scrollTopAtClickRef.current;
    scrollTopAtClickRef.current = null;
    let scrollDuration = 0;

    // UNDO THE BROWSER'S JUMP — for every toggle, pin or unpin.
    //
    // The pin button still holds focus from the click. When React moves its card to
    // a new slot, Chrome scrolls the focused element back into view — instantly, and
    // with no way to opt out. Measured: scrollTop 1800 at click, 0 by the time this
    // effect runs, with the scrollport's node, its scrollHeight and the card count
    // all unchanged. That jump IS the "teleport to top", and it is also why the
    // follow-scroll below never fired even once: by the time it measured, the
    // browser had already arrived, so there was nothing left to travel.
    //
    // This runs in useLayoutEffect, before paint, so restoring the pre-click offset
    // is invisible — the jump that just happened is never shown. Pinning then
    // animates the same trip gradually; unpinning simply stays put, because being
    // dragged after a card you just demoted is not something anyone asked for.
    const scroller = gridRef.current ? findScroller(gridRef.current) : null;
    if (scroller && scrollTopAtClick !== null && Math.abs(scroller.scrollTop - scrollTopAtClick) > 1) {
      scroller.scrollTop = scrollTopAtClick;
    }

    if (followId && scroller) {
      const card = cards.find((el) => el.dataset.projectId === followId);
      if (card) {
        // Scroll-invariant, so it does not matter that we just moved the scrollport.
        const cardTop = cardTopWithin(card, scroller);
        if (cardTop < scroller.scrollTop) {
          // A little breathing room above the card rather than flush to the edge.
          const target = Math.max(0, cardTop - 12);
          if (reduceMotion) {
            scroller.scrollTop = target;
          } else {
            scrollDuration = animateScrollTo(scroller, target) || 0;
          }
        }
      }
    }

    if (!orderChanged || moved.length === 0) return;
    if (reduceMotion) return;

    // Borrow the scroll's duration when following, so both settle on the same frame.
    const slideDuration = Math.max(320, scrollDuration);

    // Invert with transitions OFF. `.project-card` carries `transition: all 0.3s
    // ease`, so without suppressing it the card would animate INTO the offset —
    // sliding backwards away from its destination — instead of out of it.
    moved.forEach(({ card, dx, dy }) => {
      card.style.transition = 'none';
      card.style.transform = `translate(${dx}px, ${dy}px)`;
    });

    // One forced reflow for the whole batch commits the inverted positions.
    void grid.offsetWidth;

    moved.forEach(({ card }) => {
      const handleEnd = (event) => {
        if (event.propertyName !== 'transform') return;
        // Hand `transition: all` back to the stylesheet, or the card's hover
        // box-shadow and border transitions stay dead for the rest of the session.
        card.style.transition = '';
        card.style.transform = '';
        card.removeEventListener('transitionend', handleEnd);
      };
      card.addEventListener('transitionend', handleEnd);
      // Matches the follow-scroll's length and curve when one is running, so the
      // card arriving and the viewport arriving are a single motion. cubic-bezier
      // (0.65, 0, 0.35, 1) is the CSS equivalent of easeInOutCubic above.
      card.style.transition = `transform ${slideDuration}ms cubic-bezier(0.65, 0, 0.35, 1)`;
      card.style.transform = '';
    });
  }, [filteredProjects, animateScrollTo]);
  
  // Start/stop polling when projects change.
  // Only poll visualization-type projects — RSA projects never have an EC2
  // instance in UnrealProjectsInstances, so polling them would just hit the
  // backend, return "No instance name found for project", and consume budget
  // we'd rather spend on projects whose dot the user is actually waiting on.
  useEffect(() => {
    if (projectsToRender && projectsToRender.length > 0) {
      const projectIds = projectsToRender
        .filter(p => {
          const t = String(p?.ProjectType || '').toLowerCase();
          return t !== 'rsa-existing' && t !== 'rsa-exiting'
              && t !== 'rsa-model'    && t !== 'rsaa-model';
        })
        .map(p => String(p.Id));
      if (projectIds.length > 0) {
        startPollingRef.current(projectIds);
      } else {
        stopPollingRef.current();
      }
    } else {
      stopPollingRef.current();
    }

    // Cleanup on unmount
    return () => {
      stopPollingRef.current();
    };
  }, [projectsToRender]); // Removed startPolling and stopPolling from dependencies to prevent infinite loop
  
  // Note: We don't need to update projects on filteredProjects change
  // The main projects list is already being polled via the projects useEffect above
  // Filtering/sorting is just a view concern, not a polling concern

  const handleSortChange = useCallback((value) => {
    const [field, label, order] = value.split('|');
    setSortBy(field);
    setSortOrder(order);
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  };
  handleRefreshRef.current = handleRefresh;


  if (loading && projectsToRender.length === 0) {
    return (
      <div className="projects-list-loading">
        <div className="projects-list-loading-spinner"></div>
        Loading projects...
      </div>
    );
  }

  return (
    <div className="projects-list-container">
      {/* Header Controls. mobile-open: summoned by the bottom bar's search
          icon; hidden otherwise on phones (desktop unaffected). */}
      <div className={`projects-header${mobileToolbarOpen ? ' mobile-open' : ''}`}>
        <div className="projects-controls">
          {/* Search */}
          <div className="search-container">
            <input
              type="search"
              placeholder="Search by name or ID"
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
              ref={searchInputRef}
            />
            <i className="fas fa-search search-icon"></i>
          </div>

          {/* Filters - custom dropdowns so open list has dark background */}
          <div className="filter-controls">
            {/* filter-group--filter has no CSS of its own today; it is kept as
                the symmetric partner of filter-group--sort (which the phone
                toolbar uses to hide the sort dropdown) so the two siblings
                stay identifiable in devtools and either one can be targeted
                without first having to add a class. */}
            <div className="filter-group filter-select-wrap filter-group--filter" ref={filterDropdownRef}>
              <button
                type="button"
                className="filter-select filter-select-trigger"
                onClick={() => setOpenDropdown(openDropdown === 'filter' ? null : 'filter')}
                aria-haspopup="listbox"
                aria-expanded={openDropdown === 'filter'}
                aria-label="Filter projects"
              >
                {/* The visible "Show:" label is gone — the funnel says which kind
                    of control this is, the value says what's applied. aria-label
                    above keeps the name for screen readers. */}
                <i className="fas fa-filter filter-select-icon" aria-hidden="true" />
                <span>{filterLabel}</span>
                <i className={`fas fa-chevron-down filter-select-chevron ${openDropdown === 'filter' ? 'open' : ''}`} />
              </button>
              {openDropdown === 'filter' && (
                <div className="filter-select-dropdown-panel" role="listbox" aria-multiselectable="true">
                  {FILTER_OPTIONS.map((opt) => {
                    const checked =
                      opt.value === ALL_TYPES_VALUE
                        ? selectedTypes.size === 0
                        // Empty means everything is showing, so every type box is
                        // ticked — the checkmarks describe the grid, not the Set.
                        : selectedTypes.size === 0 || selectedTypes.has(opt.value);
                    return (
                      <div
                        key={opt.value}
                        role="option"
                        aria-selected={checked}
                        className={`filter-select-option ${checked ? 'selected' : ''}`}
                        // The panel deliberately stays open: picking two types
                        // takes two clicks. Outside-click still closes it.
                        onClick={() =>
                          setSelectedTypes((prev) =>
                            nextSelectedTypes(prev, opt.value, TYPE_VALUES),
                          )
                        }
                      >
                        <span className="filter-select-check" aria-hidden="true">
                          <i className="fas fa-check" />
                        </span>
                        <span>{opt.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="filter-group filter-select-wrap filter-group--sort" ref={sortDropdownRef}>
              <button
                type="button"
                className="filter-select filter-select-trigger"
                onClick={() => setOpenDropdown(openDropdown === 'sort' ? null : 'sort')}
                aria-haspopup="listbox"
                aria-expanded={openDropdown === 'sort'}
                aria-label="Sort projects"
              >
                {/* Carries more weight than the filter icon: "Last Viewed" reads
                    as a filter value without it. This is what tells the two
                    neighbouring pills apart now the labels are gone. */}
                <i className="fas fa-arrow-down-wide-short filter-select-icon" aria-hidden="true" />
                <span>{SORT_OPTIONS.find(o => o.value === sortOptionValue)?.label ?? 'Last Viewed'}</span>
                <i className={`fas fa-chevron-down filter-select-chevron ${openDropdown === 'sort' ? 'open' : ''}`} />
              </button>
              {openDropdown === 'sort' && (
                <div className="filter-select-dropdown-panel" role="listbox">
                  {SORT_OPTIONS.map((opt) => (
                    <div
                      key={opt.value}
                      role="option"
                      aria-selected={sortOptionValue === opt.value}
                      className={`filter-select-option ${sortOptionValue === opt.value ? 'selected' : ''}`}
                      onClick={() => {
                        setSortOptionValue(opt.value);
                        handleSortChange(opt.value);
                        setOpenDropdown(null);
                      }}
                    >
                      {opt.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>



          {/* View Mode Toggle */}
          <div className="view-controls">
            <RefreshButton
              onRefresh={handleRefresh}
              loading={isRefreshing}
              size="medium"
              variant="secondary"
              showText={false}
              className="projects-refresh-btn"
            />
          </div>
        </div>
      </div>

      {/* Projects Grid/List */}
      <div className="projects-content gallery">
        {isRefreshing ? (
          <div className="projects-list-loading">
            <div className="loading-spinner">
            </div>
            <p>Refreshing projects...</p>
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="no-projects">
            <i className="fas fa-folder-open"></i>
            <h3>No projects found</h3>
            <p>
              {debouncedSearchTerm || selectedTypes.size > 0
                ? 'Try adjusting your search or filter criteria.' 
                : 'No projects available at the moment.'}
            </p>
          </div>
        ) : (
          <div
            ref={gridRef}
            className={`projects-grid gallery ${filteredProjects.length >= 3 ? 'fill-width' : ''}`.trim()}
          >
            {filteredProjects.map((project, index) => (
              <ProjectCard
                key={project.Id}
                project={project}
                onProjectSelect={onProjectSelect}
                onViewProject={onViewProject}
                formatDate={formatDate}
                user={user}
                customRedirectPath={customRedirectPaths[project.Id]}
                cardIndex={index}
                isPinned={pinnedIds.has(String(project.Id))}
                onTogglePin={handleTogglePin}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectsList;
